'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import cn from 'classnames'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { track } from '@/lib/analytics'
import { type ContactUsFormSchema, contactUsFormchema } from '@/types/forms'
import { IconComponent } from '../Icon/Icon'
import { Turnstile, type TurnstileHandle, turnstileSiteKey } from '../Turnstile/Turnstile'

export const Contact = () => {
  const [isSending, setIsSending] = useState(false)
  const [successful, setSuccessful] = useState(false)
  const [failed, setFailed] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactUsFormSchema>({ resolver: zodResolver(contactUsFormchema) })

  const formRef = useRef<HTMLFormElement>(null)

  const onSubmit = async (data: ContactUsFormSchema) => {
    setIsSending(true)
    setFailed(false)

    await axios
      .post('/api/forms/contact-us', { ...data, captchaToken })
      .then((response) => {
        if (response.status === 200) {
          setSuccessful(true)
          track('form_submit', { form_name: 'contact' })
          reset()
        }
      })
      // Same hole as the demo form: a rejected request used to leave the visitor
      // with a stopped spinner and no message either way.
      .catch(() => {
        setFailed(true)
        // A token is single-use, so a retry needs a fresh one.
        setCaptchaToken(null)
        turnstileRef.current?.reset()
      })
      .finally(() => {
        setIsSending(false)
      })
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="mt-4 grid grid-cols-2 gap-5 md:mt-8">
      <div>
        <div className="mt-2 grid grid-cols-1">
          <input
            id="name"
            type="text"
            {...register('firstName')}
            placeholder="Name"
            disabled={successful || isSending}
            aria-invalid={errors.firstName ? 'true' : 'false'}
            aria-describedby={errors.firstName ? 'firstName-error' : undefined}
            className={cn(
              'focus:outline-primary-100 col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-4 pr-9 text-[15px] font-medium outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed',
              {
                'outline-red-300': errors.firstName,
                'outline-gray-300': !errors.firstName,
              }
            )}
          />
          {errors.firstName && (
            <IconComponent
              aria-hidden="true"
              icon="ErrorCircle"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500"
            />
          )}
        </div>
        {errors.firstName && (
          <p id="firstName-error" className="text-14 mt-2 text-red-600">
            {errors.firstName.message}
          </p>
        )}
      </div>
      <div>
        <div className="mt-2 grid grid-cols-1">
          <input
            id="lastName"
            type="text"
            {...register('lastName')}
            placeholder="Last name"
            disabled={successful || isSending}
            aria-invalid={errors.lastName ? 'true' : 'false'}
            aria-describedby={errors.lastName ? 'lastName-error' : undefined}
            className={cn(
              'focus:outline-primary-100 col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-4 pr-9 text-[15px] font-medium outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed',
              {
                'outline-red-300': errors.lastName,
                'outline-gray-300': !errors.lastName,
              }
            )}
          />
          {errors.lastName && (
            <IconComponent
              aria-hidden="true"
              icon="ErrorCircle"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500"
            />
          )}
        </div>
        {errors.lastName && (
          <p id="lastName-error" className="text-14 mt-2 text-red-600">
            {errors.lastName.message}
          </p>
        )}
      </div>
      <div>
        <div className="mt-2 grid grid-cols-1">
          <input
            id="email"
            type="email"
            {...register('email')}
            placeholder="Email"
            disabled={successful || isSending}
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={cn(
              'focus:outline-primary-100 col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-4 pr-9 text-[15px] font-medium outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed',
              {
                'outline-red-300': errors.email,
                'outline-gray-300': !errors.email,
              }
            )}
          />
          {errors.email && (
            <IconComponent
              aria-hidden="true"
              icon="ErrorCircle"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500"
            />
          )}
        </div>
        {errors.email && (
          <p id="email-error" className="text-14 mt-2 text-red-600">
            {errors.email.message}
          </p>
        )}
      </div>
      <div>
        <div className="mt-2 grid grid-cols-1">
          <input
            id="subject"
            type="text"
            placeholder="Subject"
            disabled={successful || isSending}
            {...register('subject')}
            aria-invalid={errors.subject ? 'true' : 'false'}
            aria-describedby={errors.subject ? 'subject-error' : undefined}
            className={cn(
              'focus:outline-primary-100 col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-4 pr-9 text-[15px] font-medium outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed',
              {
                'outline-red-300': errors.subject,
                'outline-gray-300': !errors.subject,
              }
            )}
          />
          {errors.subject && (
            <IconComponent
              aria-hidden="true"
              icon="ErrorCircle"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500"
            />
          )}
        </div>
        {errors.subject && (
          <p id="subject-error" className="text-14 mt-2 text-red-600">
            {errors.subject.message}
          </p>
        )}
      </div>
      <div className="col-span-2">
        <div className="mt-2 grid grid-cols-1">
          <textarea
            id="content"
            rows={6}
            placeholder="Message"
            disabled={successful || isSending}
            {...register('content')}
            aria-invalid={errors.content ? 'true' : 'false'}
            aria-describedby={errors.content ? 'email-error' : undefined}
            className={cn(
              'focus:outline-primary-100 col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-4 pr-9 text-[15px] font-medium outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed',
              {
                'outline-red-300': errors.content,
                'outline-gray-300': !errors.content,
              }
            )}
          />
          {errors.content && (
            <IconComponent
              aria-hidden="true"
              icon="ErrorCircle"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500"
            />
          )}
        </div>
        {errors.content && (
          <p id="content-error" className="text-14 mt-2 text-red-600">
            {errors.content.message}
          </p>
        )}
      </div>
      <div className="col-span-2">
        <Turnstile ref={turnstileRef} onToken={setCaptchaToken} />
      </div>
      <div>
        <button
          type="submit"
          disabled={successful || isSending || (!!turnstileSiteKey && !captchaToken)}
          className="button bg-primary text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send message
        </button>
      </div>

      {successful && (
        <p className="text-14 -mt-4 text-left font-semibold text-green-600">
          Your message has been sent successfully!
        </p>
      )}
      {failed && (
        <p role="alert" className="text-14 -mt-4 text-left font-semibold text-red-600">
          Something went wrong and your message was not sent. Please try again, or email support@signital.com.
        </p>
      )}
    </form>
  )
}
